import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import type { Document } from '@contentful/rich-text-types'
import CfSectionShell from '../cf-section-shell'
import type { CfSectionShellFields, PaddingToken } from '../cf-section-shell'
import type { CfImage } from '~/types/contentful'
import { renderOption } from '~/utils/contentful'
import clsx from 'clsx'
import { CfMedia } from '../cf-media'

type Columns = {
  fields: {
    headline: string
    copy: Document
    image: CfImage
    reverse: boolean
    illustrationWidth: number
    textWidth: number
  }
}

export type StepFlowFields = {
  headline: string
  subHeadline: string
  spacingBetween: PaddingToken //Space Between The Columns
  dontAlternate: boolean
  columns: Columns[]
}

interface CfStepFlowSectionProps {
  fields: CfSectionShellFields & StepFlowFields
}

export function CfStepFlowSection({ fields }: CfStepFlowSectionProps) {
  const { id, paddingTop, paddingBottom, showBottomBorder, theme } = fields
  const { headline, subHeadline, columns, dontAlternate, spacingBetween } =
    fields

  return (
    <CfSectionShell
      id={id}
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
      showBottomBorder={showBottomBorder}
      theme={theme}
    >
      <div className={clsx(dontAlternate && 'dont-alternate', 'callout-list')}>
        {typeof headline === 'string' ? (
          <hgroup className={clsx('section-header', 'mb-' + spacingBetween)}>
            <h3 className="heading-xl">{headline}</h3>
            {typeof subHeadline === 'string' ? (
              <p className="body--l">{subHeadline}</p>
            ) : null}
          </hgroup>
        ) : null}

        {Array.isArray(columns) &&
          columns
            .filter((column) => Boolean(column?.fields))
            .map((column: Columns, idx, filteredColumns) => {
              let {
                copy,
                headline,
                illustrationWidth,
                image,
                reverse,
                textWidth,
              } = column.fields
              const imageUrl = image?.fields?.file?.url

              return (
                <div
                  key={headline + '-' + idx}
                  className={clsx(
                    'callout',
                    idx < filteredColumns.length - 1 && 'mb-' + spacingBetween,
                    idx > 0 && 'mt-' + spacingBetween,
                    reverse ? 'reverse' : '',
                  )}
                  style={
                    {
                      '--illustration-column-width': illustrationWidth + '%',
                      '--text-content-column-width': textWidth + '%',
                    } as React.CSSProperties
                  }
                >
                  <div className="callout-text">
                    <hgroup>
                      <h4 className="heading-l">{headline}</h4>

                      {copy && documentToReactComponents(copy, renderOption)}
                    </hgroup>
                  </div>

                  {imageUrl ? (
                    <div className="callout-illustration">
                      <CfMedia media={image} />
                    </div>
                  ) : null}
                </div>
              )
            })}
      </div>
    </CfSectionShell>
  )
}
